/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-02-09
 */

// Add third-party dependencies.
import { useEffect } from 'react';
import { Box } from '@mui/material';

/**
 * ChatWidget component.
 * @example
 * return (
 *   <ChatWidget />
 * )
 * @returns {React.ReactElement} component.
 */
const ChatWidget = () => {
  // Chat widget script injection.
  useEffect(() => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://widgets.leadconnectorhq.com/loader.js"]');
    if (!existingScript) {
      // Wait for the chat-widget element to be in the DOM before loading the script
      const chatWidgetElement = document.querySelector('chat-widget');
      if (!chatWidgetElement) {
        console.warn('Chat widget element not found in DOM');
        return undefined;
      }

      // Add a small delay to ensure the DOM is fully ready
      const timeoutId = setTimeout(() => {
        const script = document.createElement('script');
        script.src = 'https://widgets.leadconnectorhq.com/loader.js';
        script.async = true;
        script['data-resources-url'] = 'https://widgets.leadconnectorhq.com/chat-widget/loader.js';
        script.onerror = (error) => {
          console.warn('Chat widget failed to load:', error);
        };
        document.body.appendChild(script);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        // Find and remove the script if it exists
        const scriptToRemove = document.querySelector('script[src="https://widgets.leadconnectorhq.com/loader.js"]');
        if (scriptToRemove && scriptToRemove.parentNode) {
          try {
            document.body.removeChild(scriptToRemove);
          } catch (error) {
            console.warn('Error removing chat widget script:', error);
          }
        }
      };
    }
    return undefined;
  }, []);

  /**
   * Formats the chat widget by changing the background color and text color.
   */
  const formatWidget = () => {
    try {
      const bgcolor = '#4F0B7B';
      const chatWidget = document.querySelector('chat-widget.hydrated');

      if (!chatWidget || !chatWidget.shadowRoot) {
        return;
      }

      const { shadowRoot } = chatWidget;

      // Update button background
      const btn = shadowRoot.querySelector('#lc_text-widget--btn');
      if (btn) {
        btn.style.backgroundColor = bgcolor;
      }

      // Update heading background
      const heading = shadowRoot.querySelector('div.lc_text-widget_heading--root');
      if (heading) {
        heading.style.backgroundColor = bgcolor;
      }

      // Update send button background
      const sendBtn = shadowRoot.querySelector('#lc_text-widget--send-btn');
      if (sendBtn) {
        sendBtn.style.backgroundColor = bgcolor;
      }

      // Update form label colors
      const label1 = shadowRoot.querySelector('#msgsndr_message-form > div:nth-child(1) > label');
      if (label1) {
        label1.style.color = bgcolor;
      }

      const label2 = shadowRoot.querySelector('#msgsndr_message-form > div:nth-child(2) > label');
      if (label2) {
        label2.style.color = bgcolor;
      }
    } catch (error) {
      console.warn('Error formatting chat widget:', error);
    }
  };

  // Format the chat widget.
  useEffect(() => {
    const timer = setInterval(formatWidget, 100);
    return () => {
      clearInterval(timer);
    };
  }, []);

  // Render component.
  return (
    <Box
      component="chat-widget"
      location-id="9MwYC27wHfGoNjrXqRvy"
      heading="Have a question about our dance programs?"
      sub-heading="Enter your question below and someone from our studio will contact you shortly! 😊"
      prompt-msg="Hi there, have a question about our dance programs? Text us here."
      support-contact="(818) 654-4140"
      success-msg="Someone from our studio will contact you shortly."
      prompt-avatar="https://firebasestorage.googleapis.com/v0/b/highlevel-backend.appspot.com/o/locationPhotos%2F9MwYC27wHfGoNjrXqRvy%2Fchat-widget-person?alt=media&token=56f7365b-0fa7-4101-9264-e08cfc848130"
    />
  );
};

// Export component.
export default ChatWidget;
